import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Check, MoreHorizontal, Pencil, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { CONFORMITY_LABELS, REQUEST_HISTORY_LABELS, choiceNeedsReason, dateText, money, procurementError } from "../lib/procurement";
import { formatDateTime } from "../lib/tickets";
import { Amount, FileLinks, RequestStatusBadge, RequestSteps } from "../components/procurement/RequestBits";
import {
  AssetsDialog, ConfirmDialog, ExpenseDialog, InvoiceDialog, OrderDialog, QuoteDialog, ReasonDialog, ReceptionDialog,
} from "../components/procurement/RequestDialogs";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  ResponsiveDropdownMenu, ResponsiveDropdownMenuContent, ResponsiveDropdownMenuItem, ResponsiveDropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

// The one action to offer first, then the others in the « … » menu: the server says what is possible (`capabilities`), this only orders it.
const PRIMARY_ORDER = [
  ["submit", "Envoyer pour approbation"], ["approve", "Approuver"], ["order", "Passer la commande"], ["receive", "Enregistrer une livraison"],
  ["invoice", "Saisir la facture"], ["record_expense", "Enregistrer la dépense"], ["close", "Clôturer la demande"],
];

// A label and its value: side by side in the wide column, stacked in the narrow side column (a value never gets 4 lines of 2 words).
function Fact({ label, children, stacked }) {
  return (
    <div className={`flex flex-col gap-0.5 py-1.5 ${stacked ? "" : "sm:flex-row sm:items-baseline sm:gap-3"}`}>
      <dt className={`text-xs text-slate-500 shrink-0 ${stacked ? "" : "sm:text-sm sm:w-40"}`}>{label}</dt>
      <dd className="text-sm text-slate-900 min-w-0 break-words">{children}</dd>
    </div>
  );
}

function Step({ title, children, testid }) {
  return <Card className="p-4 sm:p-5" data-testid={testid}><h2 className="font-semibold text-slate-900 mb-2">{title}</h2>{children}</Card>;
}

export default function ProcurementRequest() {
  const { requestId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const { data } = await api.get(`/procurement/requests/${requestId}`);
      setRequest(data);
      setPhase("ready");
    } catch (e) {
      setError(e?.response?.status === 404 ? "Cette demande n'existe pas, ou vous n'avez pas le droit de la voir." : procurementError(e, "Impossible de charger la demande."));
      setPhase("error");
    }
  }, [requestId]);
  useEffect(() => { load(); }, [load]);

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-10 w-full" /><Skeleton className="h-64 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3 max-w-xl mx-auto">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700" data-testid="request-error">{error}</p>
        <div className="flex justify-center gap-2"><Button variant="outline" asChild><Link to="/achats/demandes">Retour aux demandes</Link></Button><Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button></div>
      </Card>
    );
  }

  const caps = request.capabilities;
  const done = (data) => { setDialog(null); setRequest(data); if (data.created_assets?.length) toast.success(`${data.created_assets.length} actif${data.created_assets.length > 1 ? "s" : ""} ajouté${data.created_assets.length > 1 ? "s" : ""} : ${data.created_assets.map((a) => a.number).join(", ")}`); };
  const post = (path, body) => async () => (await api.post(`/procurement/requests/${request.id}/${path}`, { expected_version: request.version, ...body })).data;
  const primary = PRIMARY_ORDER.find(([id]) => caps[id]);
  const open = (name) => () => setDialog(name);
  const act = { submit: async () => { try { done(await post("submit", {})()); toast.success("Demande envoyée pour approbation"); } catch (e) { toast.error(procurementError(e, "Envoi impossible. Réessayez.")); } }, approve: () => setDialog("approve"), order: open("order"), receive: open("receive"), invoice: open("invoice"), record_expense: open("expense"), close: open("close") };
  const selected = request.quotes.find((q) => q.selected);
  const others = [
    caps.edit && { id: "edit", label: "Modifier la demande", to: `/achats/demandes/${request.id}/modifier` },
    caps.manage_quotes && { id: "quote", label: "Ajouter un devis", run: open("quote") },
    caps.approve && { id: "reject", label: "Refuser la demande", run: open("reject") },
    caps.invoice && primary?.[0] !== "invoice" && { id: "invoice", label: "Saisir la facture (livraison partielle)", run: open("invoice") },
    caps.create_assets && { id: "assets", label: "Créer des actifs depuis la livraison", run: open("assets") },
    caps.close && primary?.[0] !== "close" && { id: "close", label: "Clôturer sans dépense", run: open("close") },
    caps.cancel && { id: "cancel", label: "Annuler la demande", run: open("cancel"), danger: true },
    request.status === "draft" && request.quotes.length === 0 && hasPermission(user, "procurement:delete") && { id: "delete", label: "Supprimer la demande…", run: open("delete"), danger: true },
  ].filter(Boolean);
  const canOpenFinance = request.expense?.id && hasPermission(user, ["finance:view_all", "finance:create_expense"]);

  return (
    <div className="space-y-4" data-testid="request-page">
      <div>
        <Link to="/achats/demandes" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Demandes d'achat</Link>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-1">
          <div className="min-w-0">
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="request-title">{request.object}</h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
              <span className="tabular-nums">{request.number}</span><RequestStatusBadge status={request.status} />
              {request.status === "invoiced" && request.reception_open && <span className="text-sky-700" data-testid="request-reception-open">Livraison encore en cours</span>}
              <span>Demandé par {request.requester_name}</span>
              {request.needed_by && <span className={request.late ? "text-rose-700 font-medium" : ""}>{request.late ? "En retard — " : ""}pour le {dateText(request.needed_by)}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {primary && <Button onClick={act[primary[0]]} data-testid={`request-action-${primary[0]}`}>{primary[1]}</Button>}
            {others.length > 0 && (
              <ResponsiveDropdownMenu>
                <ResponsiveDropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Autres actions" data-testid="request-more"><MoreHorizontal size={16} /></Button>
                </ResponsiveDropdownMenuTrigger>
                <ResponsiveDropdownMenuContent align="end" title="Autres actions">
                  {others.map((o) => (o.to
                    ? <ResponsiveDropdownMenuItem key={o.id} asChild data-testid={`request-action-${o.id}`}><Link to={o.to}><Pencil size={14} className="mr-2" />{o.label}</Link></ResponsiveDropdownMenuItem>
                    : <ResponsiveDropdownMenuItem key={o.id} onClick={o.run} className={o.danger ? "text-red-600" : ""} data-testid={`request-action-${o.id}`}>{o.label}</ResponsiveDropdownMenuItem>))}
                </ResponsiveDropdownMenuContent>
              </ResponsiveDropdownMenu>
            )}
          </div>
        </div>
      </div>
      <RequestSteps status={request.status} />

      {request.status === "draft" && !selected && caps.manage_quotes && (
        <Card className="p-3 text-sm text-slate-700 border-amber-200 bg-amber-50" data-testid="request-hint-quote">Ajoutez au moins un devis et retenez-le : c'est lui que l'approbateur jugera.</Card>
      )}
      {request.status === "rejected" && request.review && (
        <Card className="p-3 text-sm border-rose-200 bg-rose-50 text-rose-900" data-testid="request-rejected">Refusée par {request.review.by_name} : {request.review.reason}. Modifiez la demande pour la renvoyer.</Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-4 min-w-0">
          <Step title="Ce qu'il faut acheter" testid="request-lines">
            <p className="text-sm text-slate-600 whitespace-pre-wrap mb-3">{request.justification}</p>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Article</TableHead><TableHead className="text-right">Qté</TableHead><TableHead className="hidden sm:table-cell text-right">Prix estimé</TableHead>
                <TableHead className="text-right">Total</TableHead>{["ordered", "partially_received", "received", "invoiced", "closed"].includes(request.status) && <TableHead className="text-right">Reçu</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {request.lines.map((line) => (
                  <TableRow key={line.id} data-testid="request-line">
                    <TableCell className="font-medium text-slate-900 break-words">{line.label}</TableCell>
                    <TableCell className="text-right tabular-nums">{line.quantity}</TableCell>
                    <TableCell className="hidden sm:table-cell text-right tabular-nums">{money(line.unit_price_minor, request.currency)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(line.total_minor, request.currency)}</TableCell>
                    {["ordered", "partially_received", "received", "invoiced", "closed"].includes(request.status) && (
                      <TableCell className="text-right tabular-nums">{line.received} / {line.quantity}{line.quantity === line.received ? <Check size={12} className="inline ml-1 text-emerald-600" aria-label="Complet" /> : null}</TableCell>
                    )}
                  </TableRow>
                ))}
                <TableRow><TableCell colSpan={2} className="text-sm text-slate-500">Total estimé{request.project ? ` · ${request.project}` : ""}</TableCell><TableCell className="hidden sm:table-cell" />
                  <TableCell className="text-right font-semibold tabular-nums" data-testid="request-total">{money(request.total_minor, request.currency)}</TableCell>{["ordered", "partially_received", "received", "invoiced", "closed"].includes(request.status) && <TableCell />}</TableRow>
              </TableBody>
            </Table>
          </Step>

          <Step title="Devis" testid="request-quotes">
            {request.quotes.length === 0 ? (
              <div className="text-center py-4 space-y-2" data-testid="request-quotes-empty">
                <p className="text-sm text-slate-500">Aucun devis pour le moment.</p>
                {caps.manage_quotes && <Button variant="outline" size="sm" onClick={open("quote")} data-testid="quote-add"><Plus size={14} className="mr-1.5" />Ajouter un devis</Button>}
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2" data-testid="quote-list">
                {request.quotes.map((q) => (
                  <div key={q.id} className={`rounded-lg border p-3 space-y-1.5 ${q.selected ? "border-jamora bg-jamora/5" : "border-slate-200"}`} data-testid={`quote-${q.selected ? "selected" : "other"}`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium text-slate-900 break-words">{q.supplier.name}</span>
                      {q.selected && <span className="rounded-full bg-jamora text-white px-2 py-0.5 text-xs">Retenu</span>}
                      {q.cheapest && request.quotes.length > 1 && <span className="rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-2 py-0.5 text-xs">Le moins cher</span>}
                    </div>
                    <Amount minor={q.amount_minor} currency={q.currency} className="block text-lg font-semibold text-slate-900" />
                    <div className="text-xs text-slate-500 space-y-0.5">
                      {q.reference && <div>Réf. {q.reference}</div>}
                      {q.valid_until && <div className={q.expired ? "text-rose-700 font-medium" : ""}>{q.expired ? "Expiré le " : "Valable jusqu'au "}{dateText(q.valid_until)}</div>}
                      <div>Saisi par {q.created_by_name}</div>
                    </div>
                    {q.note && <p className="text-sm text-slate-600 whitespace-pre-wrap">{q.note}</p>}
                    <FileLinks files={q.files} />
                    {q.selected && request.selection_reason && <p className="text-xs text-slate-600">Retenu malgré un devis moins cher : {request.selection_reason}</p>}
                    {caps.manage_quotes && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {!q.selected && <Button size="sm" variant="outline" onClick={() => setDialog({ select: q })} data-testid="quote-select">Retenir ce devis</Button>}
                        <Button size="sm" variant="ghost" className="text-slate-600" onClick={() => setDialog({ remove: q })} data-testid="quote-remove">Retirer</Button>
                      </div>
                    )}
                  </div>
                ))}
                {caps.manage_quotes && request.quotes.length < 10 && (
                  <button type="button" onClick={open("quote")} className="rounded-lg border border-dashed border-slate-300 p-3 text-sm text-slate-500 hover:bg-slate-50 min-h-[5rem]" data-testid="quote-add">
                    <Plus size={14} className="inline mr-1" />Ajouter un devis
                  </button>
                )}
              </div>
            )}
          </Step>

          {request.order && (
            <Step title="Commande" testid="request-order">
              <dl className="divide-y divide-slate-100">
                <Fact label="Commandé le">{dateText(request.order.ordered_on)} par {request.order.by_name}</Fact>
                {request.order.reference && <Fact label="Bon de commande">{request.order.reference}</Fact>}
                {request.order.expected_delivery_on && <Fact label="Livraison prévue">{dateText(request.order.expected_delivery_on)}</Fact>}
              </dl>
            </Step>
          )}

          {request.receptions.length > 0 && (
            <Step title="Livraisons" testid="request-receptions">
              <ul className="space-y-3">
                {request.receptions.map((r) => (
                  <li key={r.id} className="border-l-2 border-slate-200 pl-3" data-testid="reception">
                    <div className="text-sm text-slate-900">Reçu le {dateText(r.received_on)} par {r.received_by.name} · <span className={r.conformity === "claim" ? "text-rose-700 font-medium" : "text-emerald-700"}>{CONFORMITY_LABELS[r.conformity]}</span></div>
                    <div className="text-xs text-slate-500">{r.lines.map((l) => `${l.quantity} × ${request.lines.find((x) => x.id === l.line_id)?.label || "article"}`).join(", ")}</div>
                    {r.note && <div className="text-sm text-slate-600 whitespace-pre-wrap">{r.note}</div>}
                  </li>
                ))}
              </ul>
            </Step>
          )}

          {request.invoice && (
            <Step title="Facture" testid="request-invoice">
              <dl className="divide-y divide-slate-100">
                <Fact label="Référence">{request.invoice.reference} · {dateText(request.invoice.issued_on)}</Fact>
                <Fact label="Montant"><Amount minor={request.invoice.amount_minor} currency={request.invoice.currency} />
                  {request.invoice.differs_from_quote && <span className="block text-xs text-amber-700" data-testid="invoice-differs">Différent du devis retenu ({money(request.invoice.quote_minor, request.invoice.currency)}).</span>}</Fact>
                <Fact label="Document"><FileLinks files={request.invoice.files} /></Fact>
                {request.expense && (
                  <Fact label="Dépense"><span data-testid="request-expense">Enregistrée dans les finances (brouillon à vérifier).</span>
                    {canOpenFinance && <Link to="/finance" state={{ openExpense: request.expense.id }} className="ml-2 text-xs text-jamora underline" data-testid="request-expense-link">Ouvrir</Link>}</Fact>
                )}
              </dl>
            </Step>
          )}
        </div>

        <aside className="space-y-4 min-w-0">
          <Card className="p-4 sm:p-5 space-y-2" data-testid="request-summary">
            <h2 className="font-semibold text-slate-900">Où en est la demande</h2>
            <dl className="divide-y divide-slate-100">
              <Fact stacked label="Estimé"><Amount minor={request.total_minor} currency={request.currency} /></Fact>
              {selected && <Fact stacked label="Devis retenu"><Amount minor={selected.amount_minor} currency={selected.currency} /> · {selected.supplier.name}</Fact>}
              {request.approval && <Fact stacked label="Approuvée par"><span data-testid="request-approval">{request.approval.by_name}, le {formatDateTime(request.approval.at)}</span>{request.approval.reason && <span className="block text-xs text-slate-500">{request.approval.reason}</span>}</Fact>}
              {request.closure && <Fact stacked label="Clôturée">{request.closure.by_name}{request.closure.reason ? ` — ${request.closure.reason}` : ""}</Fact>}
              {request.cancellation && <Fact stacked label="Annulée"><span data-testid="request-cancellation">{request.cancellation.by_name} — {request.cancellation.reason}</span></Fact>}
              <Fact stacked label="Créée le">{formatDateTime(request.created_at)}</Fact>
            </dl>
            {request.status === "submitted" && !caps.approve && <p className="text-xs text-slate-500">Elle attend la décision d'une personne habilitée à l'approuver.</p>}
          </Card>
          <Card className="p-4 sm:p-5" data-testid="request-history">
            <h2 className="font-semibold text-slate-900 mb-3">Historique</h2>
            <ol className="space-y-3">
              {request.history.map((h) => (
                <li key={h.id} className="border-l-2 border-slate-200 pl-3">
                  <div className="text-sm text-slate-900">{REQUEST_HISTORY_LABELS[h.kind] || h.kind}{h.data?.supplier ? ` — ${h.data.supplier}` : ""}</div>
                  <div className="text-xs text-slate-500">{formatDateTime(h.at)}{h.actor_name ? ` · ${h.actor_name}` : ""}</div>
                  {h.reason && <div className="text-xs text-slate-600 mt-0.5">Motif : {h.reason}</div>}
                </li>
              ))}
            </ol>
          </Card>
        </aside>
      </div>

      {dialog === "quote" && <QuoteDialog request={request} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "order" && <OrderDialog request={request} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "receive" && <ReceptionDialog request={request} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "assets" && <AssetsDialog request={request} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "invoice" && <InvoiceDialog request={request} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "expense" && <ExpenseDialog request={request} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "approve" && (
        <ReasonDialog title="Approuver la demande" description={`${request.number} — ${selected ? `devis retenu de ${selected.supplier.name}, ${money(selected.amount_minor, selected.currency)}` : ""}`}
          label="Commentaire" optional confirm="Approuver" onSubmit={(reason) => post("approve", { reason })()} onClose={() => setDialog(null)} onDone={done} success="Demande approuvée" />
      )}
      {dialog === "reject" && (
        <ReasonDialog title="Refuser la demande" description="Le demandeur verra votre motif et pourra corriger puis renvoyer sa demande." confirm="Refuser" danger
          placeholder="Ce qui manque ou ce qui pose problème" onSubmit={(reason) => post("reject", { reason })()} onClose={() => setDialog(null)} onDone={done} success="Demande refusée" />
      )}
      {dialog === "cancel" && (
        <ReasonDialog title="Annuler la demande" description="Une demande annulée ne se rouvre pas : elle reste dans l'historique avec son motif." confirm="Annuler la demande" danger
          onSubmit={(reason) => post("cancel", { reason })()} onClose={() => setDialog(null)} onDone={done} success="Demande annulée" />
      )}
      {dialog === "close" && (
        <ReasonDialog title="Clôturer la demande" optional={!!request.expense}
          description={request.expense ? "La dépense est enregistrée : la demande est terminée." : "Aucune dépense n'est enregistrée dans les finances : dites pourquoi (payé autrement, finances non activées…)."}
          confirm="Clôturer" onSubmit={(reason) => post("close", { reason })()} onClose={() => setDialog(null)} onDone={done} success="Demande clôturée" />
      )}
      {dialog?.select && (
        <ReasonDialog title="Retenir ce devis" optional={!choiceNeedsReason(dialog.select, request.quotes)}
          description={choiceNeedsReason(dialog.select, request.quotes) ? `${dialog.select.supplier.name} n'est pas le moins cher : dites pourquoi vous le préférez.` : `${dialog.select.supplier.name} est le moins cher.`}
          confirm="Retenir" onSubmit={(reason) => post(`quotes/${dialog.select.id}/select`, { reason })()} onClose={() => setDialog(null)} onDone={done} success="Devis retenu" />
      )}
      <DeleteWithReasonDialog
        target={dialog === "delete" ? request : null} testId="delete-request" confirmLabel="Supprimer définitivement" onClose={() => setDialog(null)}
        title={`Supprimer la demande ${request?.number} ?`}
        description="Elle est effacée définitivement : un brouillon sans devis n'a encore rien produit. Une demande envoyée, ou avec un devis, s'annule à la place pour garder son historique."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/procurement/requests/${request.id}`, { data: { reason } });
            toast.success("Demande supprimée");
            navigate("/achats/demandes");
          } catch (e) {
            toast.error(procurementError(e, "Impossible de supprimer cette demande."));
            setDialog(null);
          }
        }}
      />
      {dialog?.remove && (
        <ConfirmDialog title="Retirer ce devis ?" description={`Le devis de ${dialog.remove.supplier.name} sort de la demande${dialog.remove.selected ? " ; il faudra en retenir un autre" : ""}${request.status === "approved" && dialog.remove.selected ? ", et l'approbation sera annulée" : ""}.`}
          confirm="Retirer" danger onSubmit={post(`quotes/${dialog.remove.id}/remove`, {})} onClose={() => setDialog(null)} onDone={done} success="Devis retiré" />
      )}
    </div>
  );
}
